// SEO / JSON-LD helpers. Inject <script type="application/ld+json"> into <head>
// and set title + meta tags per page. National positioning across Australia.
import { useEffect } from "react";

const SITE_URL = "https://alphacommunitycare.com.au";
const PHONE = "+61-1300-380-692";
const BRAND = "Alpha Community Care";

const AU_STATES = [
  { "@type": "State", "name": "Victoria" },
  { "@type": "State", "name": "Western Australia" },
  { "@type": "State", "name": "New South Wales" },
  { "@type": "State", "name": "Queensland" },
  { "@type": "State", "name": "South Australia" },
  { "@type": "State", "name": "Australian Capital Territory" },
  { "@type": "State", "name": "Tasmania" },
  { "@type": "State", "name": "Northern Territory" },
];

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": BRAND,
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo.png`,
    "description":
      "Alpha Community Care is a registered NDIS provider servicing participants across Australia, with operational hubs in Melbourne (VIC) and Perth (WA).",
    "telephone": PHONE,
    "email": "hello@alphacommunitycare.com.au",
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": "Docklands",
        "addressLocality": "Melbourne",
        "addressRegion": "VIC",
        "postalCode": "3008",
        "addressCountry": "AU",
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "West Perth",
        "addressLocality": "Perth",
        "addressRegion": "WA",
        "postalCode": "6005",
        "addressCountry": "AU",
      },
    ],
    "areaServed": {
      "@type": "Country",
      "name": "Australia",
      "containsPlace": AU_STATES,
    },
    "sameAs": [],
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
    "name": BRAND,
    "url": SITE_URL,
    "image": `${SITE_URL}/og-cover.jpg`,
    "telephone": PHONE,
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Docklands",
      "addressLocality": "Melbourne",
      "addressRegion": "VIC",
      "postalCode": "3008",
      "addressCountry": "AU",
    },
    "areaServed": {
      "@type": "Country",
      "name": "Australia",
      "containsPlace": AU_STATES,
    },
    "openingHours": "Mo-Fr 08:00-18:00",
  };
}

export function serviceSchema({ name, description, slug, city }) {
  const url = city ? `${SITE_URL}/${slug}-${city.slug}` : `${SITE_URL}/${slug}`;
  const area = city
    ? { "@type": "City", "name": city.name, "containedInPlace": { "@type": "State", "name": city.state } }
    : { "@type": "Country", "name": "Australia", "containsPlace": AU_STATES };
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": name,
    "name": `${name} | ${BRAND}`,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": BRAND,
      "url": SITE_URL,
      "telephone": PHONE,
    },
    "areaServed": area,
    "url": url,
    "termsOfService": `${SITE_URL}/terms`,
  };
}

export function articleSchema({ title, description, slug, date, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "author": { "@type": "Organization", "name": BRAND, "url": SITE_URL },
    "publisher": {
      "@type": "Organization",
      "name": BRAND,
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.png` },
    },
    "datePublished": date,
    "mainEntityOfPage": `${SITE_URL}/resources/${slug}`,
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": `${SITE_URL}${it.path}`,
    })),
  };
}

// --- React helper ----------------------------------------------------------
function ensureMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function ensureCanonical(url) {
  if (typeof document === "undefined") return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

const LD_SCRIPT_ID_PREFIX = "alpha-ld-";

function setJsonLd(id, obj) {
  if (typeof document === "undefined") return;
  const scriptId = `${LD_SCRIPT_ID_PREFIX}${id}`;
  let el = document.getElementById(scriptId);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = scriptId;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function clearPageJsonLd() {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll(`script[id^="${LD_SCRIPT_ID_PREFIX}page-"]`)
    .forEach((el) => el.remove());
}

/**
 * Usage:
 *   useSEO({
 *     title: "...",
 *     description: "...",
 *     keywords: ["..."],
 *     canonical: "https://alphacommunitycare.com.au/sil",
 *     jsonLd: [serviceSchema({...}), breadcrumbSchema([...])]
 *   });
 */
export function useSEO({ title, description, keywords, canonical, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) ensureMeta("description", description);
    if (keywords && keywords.length) ensureMeta("keywords", keywords.join(", "));
    if (title) ensureMeta("og:title", title, "property");
    if (description) ensureMeta("og:description", description, "property");
    ensureMeta("og:site_name", BRAND, "property");
    ensureMeta("og:type", "website", "property");
    if (canonical) {
      ensureMeta("og:url", canonical, "property");
      ensureCanonical(canonical);
    }
    ensureMeta("twitter:card", "summary_large_image");
    if (title) ensureMeta("twitter:title", title);
    if (description) ensureMeta("twitter:description", description);

    clearPageJsonLd();
    if (jsonLd && jsonLd.length) {
      jsonLd.forEach((obj, i) => setJsonLd(`page-${i}`, obj));
    }
  }, [title, description, canonical, JSON.stringify(keywords), JSON.stringify(jsonLd)]);
}

// Mount once (in App.js) to emit site-wide Organization + LocalBusiness schema.
export function SiteStructuredData() {
  useEffect(() => {
    setJsonLd("site-org", organizationSchema());
    setJsonLd("site-local", localBusinessSchema());
  }, []);
  return null;
}

export { SITE_URL, BRAND };
